import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import Layout from '../components/Layout';
import SEO, { aboutPageSEO } from '../components/SEO';

const About: React.FC = () => {
  const features = [
    {
      title: '專業團隊',
      description: '擁有豐富經驗的金融專家團隊，深入了解市場動態',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      title: '快速服務',
      description: '24小時內回覆，快速審核，讓您及時獲得所需資金',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: '安全可靠',
      description: '嚴格的資料保護措施，確保您的個人資訊安全無虞',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: '客製方案',
      description: '根據每位客戶的獨特需求，量身打造最適合的貸款方案',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      )
    }
  ];

  const process = [
    {
      step: '1',
      title: '免費諮詢',
      description: '透過 LINE 官方帳號或線上表單聯繫我們，獲得免費初步諮詢'
    },
    {
      step: '2',
      title: '需求評估',
      description: '專業顧問深入了解您的財務狀況和資金需求'
    },
    {
      step: '3',
      title: '方案推薦',
      description: '根據評估結果，為您推薦最適合的貸款方案'
    },
    {
      step: '4',
      title: '申請辦理',
      description: '協助您準備相關文件，快速完成申請流程'
    }
  ];

  return (
    <HelmetProvider>
      <SEO {...aboutPageSEO} />
      <Layout>
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">關於好野貸</h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            我們是專業的借貸服務平台，致力於為每位客戶提供最適合的貸款方案，
            讓您的資金需求得到最快速、最安全的解決方案。
          </p>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">我們的理念</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">專業服務</h3>
                  <p className="text-gray-600 leading-relaxed">
                    好野貸擁有經驗豐富的金融專業團隊，深入了解市場動態與各項貸款產品，
                    能夠為您提供專業、客觀的建議，確保您獲得最適合的貸款方案。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">客戶至上</h3>
                  <p className="text-gray-600 leading-relaxed">
                    我們始終將客戶的需求放在首位，用心傾聽每位客戶的獨特情況，
                    量身打造最適合的解決方案，讓您在整個貸款過程中感到安心與信任。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">誠信透明</h3>
                  <p className="text-gray-600 leading-relaxed">
                    我們堅持誠信經營，所有費用與條件都會清楚說明，
                    絕不隱藏任何費用，讓您能夠明明白白借款，安心還款。
                  </p>
                </div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-blue-50 rounded-lg p-8">
                <div className="bg-blue-100 text-blue-600 p-6 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">選擇好野貸的理由</h3>
                <p className="text-gray-600 mb-6">
                  我們不僅是您的貸款顧問，更是您值得信賴的財務夥伴
                </p>
                <div className="space-y-3 text-left">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">超過10年金融服務經驗</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">服務超過10,000名客戶</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">核貸率高達95%</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">24小時內快速回覆</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              我們的優勢
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              選擇好野貸，您將享受到專業、快速、安全、客製化的優質服務
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md text-center hover:shadow-lg transition-shadow duration-300">
                <div className="bg-blue-100 text-blue-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              服務流程
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              簡單四步驟，讓您輕鬆獲得最適合的貸款方案
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="text-center">
                <div className="relative">
                  <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-6 left-full w-full h-0.5 bg-blue-200 -translate-x-1/2"></div>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">聯絡我們</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            有任何問題或需要諮詢嗎？歡迎透過 LINE 官方帳號聯繫我們，
            我們的專業團隊將竭誠為您服務。
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-blue-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">服務時間</h3>
              <p className="text-blue-100 mb-2">週一至週五</p>
              <p className="text-blue-100 mb-4">上午 9:00 - 下午 18:00</p>
              <p className="text-blue-100">線上24小時留言服務</p>
            </div>
            <div className="bg-blue-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">聯絡方式</h3>
              <p className="text-blue-100 mb-2">LINE 官方帳號</p>
              <p className="text-blue-100 font-mono">@939rzwem</p>
              <p className="text-blue-100 text-sm mt-4">加入好友後即可開始諮詢</p>
            </div>
          </div>

          <a
            href="https://line.me/R/ti/p/@939rzwem"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors duration-200 shadow-lg"
          >
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            立即諮詢
          </a>
        </div>
      </section>
    </Layout>
    </HelmetProvider>
  );
};

export default About;